"use client"
import { Box, Button, ButtonGroup, Paper, Typography } from "@mui/material"
import { BarChartWithScroll } from "./bar-chart-with-scroll"
import { ProviderDetail } from "../../store/slices/provider"
import { DataListV2 } from "./data-list-v2"
import { formatDateString } from "../../utils/date"
import { useState } from "react"


interface ProviderDetailsBlockProps {
  providerName: string
  invoices: ProviderDetail[]
  onInvoiceSelect?: (invoice: ProviderDetail) => void;
  selectedCoin: 'USD' | 'PEN';
  currentView: 'Tabla' | 'Gráfico';
  setCurrentView: (value: 'Tabla' | 'Gráfico') => void

}

export function ProviderDetailsBlock({
  providerName,
  invoices,
  onInvoiceSelect,
  selectedCoin,
  currentView,
  setCurrentView,
}: ProviderDetailsBlockProps) {
  
  const [scrollIndex, setScrollIndex] = useState(0)

  const sortedInvoices = [...invoices]
    .map(p => ({ 
      ...p,
      id: p.NumeroDocumento, 
      invoiceNumber: p.NumeroDocumento, 
      amount: p[`Costo${selectedCoin}`], 
    }))
    .sort((a, b) => b.CostoPEN - a.CostoPEN)

  return (
    <Paper
      elevation={1}
      sx={{
        padding: 3,
        backgroundColor: "var(--background)",
        borderRadius: "var(--radius)",
        height: "40vh",
        display: "flex",
        flexDirection: "column",
        marginTop: 3,
      }}
    >
      <Box 
        sx={{
          display: "grid",
          gridTemplateColumns: "40% 1fr",
          gap: 3,
          flex: 1,
          maxHeight: '2em'
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: "var(--foreground)",
            marginBottom: 0.5,
          }}
        >
          Detalle - {providerName}
        </Typography>
        <ButtonGroup size="small" variant="outlined" style={{maxHeight: '1.5em'}}>
          {['Tabla', 'Gráfico'].map((m: any) => (
            <Button
              key={m}
              onClick={() => setCurrentView(m)}
              variant={currentView === m ? 'contained' : 'outlined'}
            >
              {m}
            </Button>
          ))}
        </ButtonGroup>
      </Box>

      <Box
        sx={{
          display: "grid",
          gap: 3,
          flex: 1,
          minHeight: 0,
        }}
      >
        {currentView === 'Tabla' && <Box sx={{ minHeight: 0 }}>
          <DataListV2
            data={sortedInvoices}
            columns={[
              { label: 'RUC', value: 'RUC', width: '7%'},
              { label: 'Empresa', value: 'Empresa', width: '8%' },
              { label: 'Nro Documento', value: 'NumeroDocumento', width: '10%'},
              { label: 'Fecha', value: 'FechaDocumento', width: '7%', formatValue: (val) => formatDateString(val)},
              { label: 'Proyecto', value: 'Proyecto', width: '10%'},
              { label: 'Glosa', value: 'Glosa', width: '15%'},
              { label: 'Estado', value: 'Estado', width: '10%'},
              { label: 'Moneda Origen', value: 'SimboloMoneda', width: '7%'},
              { label: 'Monto S/', value: 'CostoPEN', align: 'right', 
                formatValue: (val) => Number.parseFloat(val).toLocaleString("es-PE", { minimumFractionDigits: 2 }), width: '12%'
              },
              { label: 'Monto $', value: 'CostoUSD', align: 'right', 
                formatValue: (val) => Number.parseFloat(val).toLocaleString("es-PE", { minimumFractionDigits: 2 }), width: '12%'
              },
            ]}
            showTotal={true}
            onItemClick={(invoice) => onInvoiceSelect?.(invoice as any)}
            formatTotal={(total) => `${total.toFixed(2)}`}
          />
        </Box>}

        {currentView === 'Gráfico' && <Box sx={{ minHeight: 0 }}>
          <BarChartWithScroll
            scrollIndex={scrollIndex}
            setScrollIndex={setScrollIndex}
            data={sortedInvoices as any}
            labelField="invoiceNumber"
            valueField="amount"
            visibleItems={10}
            tooltipLabel="Monto de Factura"
            onItemClick={(invoice) => onInvoiceSelect?.(invoice as any)}
            formatValue={(value) =>
              `${Number.parseFloat(value).toLocaleString("es-PE", { minimumFractionDigits: 2 })}`
            }
            barColor="rgba(153, 102, 255, 0.8)"
          />
        </Box>}
      </Box>
    </Paper>
  )
}
